import { useState, useEffect } from 'react';
import type {
  TimelineSettings,
  ProblemSolutionSettings,
  TeamSettings,
  ProductShowcaseSettings,
  AdvancedVideoSettings,
  BackgroundSettings,
} from '../../../types/site';
import { getBackgroundStyle } from '../BackgroundSettings';
import { getProductsByIds } from '../../../services/productService';
import type { ProductWithMedia } from '../../../types/products';
import { PhosphorIcon } from '../../IconPicker';
import { logger } from '../../../lib/logger';

interface BlockWrapperProps {
  background?: BackgroundSettings;
  children: React.ReactNode;
  className?: string;
}

export function BlockWrapper({ background, children, className = '' }: BlockWrapperProps) {
  const bgStyle = background ? getBackgroundStyle(background) : {};
  const hasBackground = background && background.type !== 'none';

  if (!hasBackground) {
    return <>{children}</>;
  }

  return (
    <div className="relative" style={bgStyle}>
      <div className={className}>{children}</div>
    </div>
  );
}

interface TimelineBlockProps {
  settings: TimelineSettings;
  primaryColor: string;
}

export function TimelineBlock({ settings, primaryColor }: TimelineBlockProps) {
  if (!settings.items || settings.items.length === 0) return null;

  return (
    <BlockWrapper background={settings.background}>
      <section className="py-20 md:py-28 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
            >
              <span>Timeline</span>
            </div>
            {settings.title && (
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{settings.title}</h2>
            )}
            {settings.subtitle && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{settings.subtitle}</p>
            )}
          </div>

          <div className="relative">
            <div
              className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2"
              style={{ backgroundColor: `${primaryColor}20` }}
            />

            <div className="space-y-12">
              {settings.items.map((item, index) => (
                <div
                  key={index}
                  className={`relative flex items-start gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div
                    className="absolute left-8 md:left-1/2 w-4 h-4 rounded-full border-4 border-white shadow-md -translate-x-1/2 z-10"
                    style={{ backgroundColor: primaryColor }}
                  />

                  <div className="hidden md:block md:w-1/2" />

                  <div className={`flex-1 ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                      <div className="flex items-center gap-3 mb-4">
                        {item.icon && (
                          <span style={{ color: primaryColor }}>
                            <PhosphorIcon name={item.icon} size={28} weight="regular" />
                          </span>
                        )}
                        <span
                          className="px-3 py-1 rounded-full text-sm font-semibold"
                          style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                        >
                          {item.yearOrStep}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt=""
                          className="mt-4 rounded-xl w-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </BlockWrapper>
  );
}

interface ProblemSolutionBlockProps {
  settings: ProblemSolutionSettings;
  primaryColor: string;
}

export function ProblemSolutionBlock({ settings, primaryColor }: ProblemSolutionBlockProps) {
  const isStacked = settings.layout === 'stacked';

  return (
    <BlockWrapper background={settings.background}>
      <section className="py-20 md:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          {settings.title && (
            <div className="text-center mb-16">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
                style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
              >
                <span>Problem & Lösung</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{settings.title}</h2>
            </div>
          )}

          <div className={`${isStacked ? 'space-y-12' : 'grid md:grid-cols-2 gap-8'}`}>
            <div className="relative">
              <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-3xl p-8 border border-red-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-red-500 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-red-900">Das Problem</h3>
                </div>
                <ul className="space-y-4">
                  {(settings.problems || []).map((problem, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 text-red-600">
                        <PhosphorIcon name={problem.icon || 'X'} size={24} weight="bold" />
                      </span>
                      <span className="text-red-800">{problem.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-green-50 to-emerald-100/50 rounded-3xl p-8 border border-green-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-green-900">Die Lösung</h3>
                </div>
                <ul className="space-y-4">
                  {(settings.solutions || []).map((solution, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 text-green-600">
                        <PhosphorIcon name={solution.icon || 'Check'} size={24} weight="bold" />
                      </span>
                      <span className="text-green-800">{solution.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </BlockWrapper>
  );
}

interface TeamBlockProps {
  settings: TeamSettings;
  primaryColor: string;
}

export function TeamBlock({ settings, primaryColor }: TeamBlockProps) {
  if (!settings.members || settings.members.length === 0) return null;

  const columns = settings.columns || 3;
  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  }[columns];

  return (
    <BlockWrapper background={settings.background}>
      <section className="py-20 md:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
            >
              <span>Unser Team</span>
            </div>
            {settings.title && (
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{settings.title}</h2>
            )}
            {settings.subtitle && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{settings.subtitle}</p>
            )}
          </div>

          <div className={`grid gap-8 ${gridCols}`}>
            {settings.members.map((member, index) => (
              <div
                key={index}
                className="group bg-white rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 text-center"
              >
                {member.imageUrl && (
                  <div className="relative mb-6 mx-auto w-32 h-32">
                    <div
                      className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-20 transition-opacity blur-xl"
                      style={{ backgroundColor: primaryColor }}
                    />
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      className="relative w-full h-full rounded-full object-cover ring-4 ring-gray-50 group-hover:ring-8 transition-all"
                    />
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                {member.role && (
                  <p className="text-sm font-medium mb-3" style={{ color: primaryColor }}>
                    {member.role}
                  </p>
                )}
                {member.bio && (
                  <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </BlockWrapper>
  );
}

interface ProductShowcaseBlockProps {
  settings: ProductShowcaseSettings;
  primaryColor: string;
}

interface DisplayProduct {
  id: string;
  imageUrl: string | null;
  name: string;
  description: string | null;
  badge: string | null;
}

export function ProductShowcaseBlock({ settings, primaryColor }: ProductShowcaseBlockProps) {
  const [displayProducts, setDisplayProducts] = useState<DisplayProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load products from database if selectedProducts is used
  useEffect(() => {
    const loadProducts = async () => {
      // Check for new format (selectedProducts from database)
      if (settings.selectedProducts && settings.selectedProducts.length > 0) {
        setIsLoading(true);
        try {
          const productIds = settings.selectedProducts.map((p) => p.productId);
          const products = await getProductsByIds(productIds);

          // Build a map for quick lookup
          const productMap: Record<string, ProductWithMedia> = {};
          products.forEach((p) => {
            productMap[p.id] = p;
          });

          // Create display products in the order they were selected
          const display: DisplayProduct[] = settings.selectedProducts
            .map((selected) => {
              const product = productMap[selected.productId];
              if (!product) return null;

              // Get first media image URL
              const imageUrl = product.media?.[0]?.url || null;

              return {
                id: product.id,
                imageUrl,
                name: selected.customName || product.name,
                description: selected.customDescription || product.short_tagline,
                badge: selected.customBadge || null,
              };
            })
            .filter((p): p is DisplayProduct => p !== null);

          setDisplayProducts(display);
        } catch (error) {
          logger.error('Failed to load products:', error);
        } finally {
          setIsLoading(false);
        }
      }
      // Legacy format (manual products array)
      else if (settings.products && settings.products.length > 0) {
        const display: DisplayProduct[] = settings.products.map((p, index) => ({
          id: `legacy-${index}`,
          imageUrl: p.imageUrl || null,
          name: p.name,
          description: p.description,
          badge: p.badge,
        }));
        setDisplayProducts(display);
      }
    };

    loadProducts();
  }, [settings.selectedProducts, settings.products]);

  // Don't render if no products
  const hasProducts = (settings.selectedProducts && settings.selectedProducts.length > 0) ||
                      (settings.products && settings.products.length > 0);
  if (!hasProducts) return null;

  const columns = settings.columns || 3;
  const gridCols = columns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3';

  return (
    <BlockWrapper background={settings.background}>
      <section className="py-20 md:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
            >
              <span>Produkte</span>
            </div>
            {settings.title && (
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{settings.title}</h2>
            )}
            {settings.subtitle && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{settings.subtitle}</p>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: primaryColor, borderTopColor: 'transparent' }} />
            </div>
          ) : (
            <div className={`grid gap-8 ${gridCols}`}>
              {displayProducts.map((product) => (
                <div
                  key={product.id}
                  className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="relative aspect-square overflow-hidden">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-16 h-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    {product.badge && (
                      <span
                        className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {product.badge}
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                    {product.description && (
                      <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </BlockWrapper>
  );
}

interface AdvancedVideoBlockProps {
  settings: AdvancedVideoSettings;
  primaryColor: string;
}

export function AdvancedVideoBlock({ settings, primaryColor }: AdvancedVideoBlockProps) {
  const getYouTubeEmbedUrl = (url: string | null) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}${settings.autoplay ? '?autoplay=1&mute=1' : ''}` : null;
  };

  const embedUrl = settings.videoType === 'youtube' ? getYouTubeEmbedUrl(settings.youtubeUrl) : null;
  const videoUrl = settings.videoType === 'upload' ? settings.videoUrl : null;

  if (!embedUrl && !videoUrl) return null;

  const rounded = settings.rounded !== false;

  return (
    <BlockWrapper background={settings.background}>
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          {settings.title && (
            <div className="text-center mb-10">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
                style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
              >
                <span>Video</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{settings.title}</h2>
            </div>
          )}

          <div className={`relative overflow-hidden shadow-2xl ${rounded ? 'rounded-3xl' : ''}`}>
            {embedUrl ? (
              <div className="aspect-video">
                <iframe
                  src={embedUrl}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            ) : videoUrl ? (
              <video
                src={videoUrl}
                className="w-full"
                controls
                autoPlay={settings.autoplay}
                muted={settings.autoplay}
                playsInline
              />
            ) : null}
          </div>
        </div>
      </section>
    </BlockWrapper>
  );
}
